import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../components/session/postSessionCard';
import { ContentCard } from '../../../components/common/ContentCard';
import {
  ScaleQuestion,
  type ScaleOption,
} from '../../../components/session/ScaleQuestion';
import { AlertTriangle, CheckCircle, Eye, FileText, Info } from 'react-feather';
import { participantPostSessionApi } from '../../../services/api/participantPostSession';
import type {
  ParticipantFeedbackPayload,
  UpsertFeedbackDraftRequest,
} from '../../../services/api/participantPostSession';
import { useFeedbackAutosave } from './useFeedbackAutosave';

// MED-1055 — shared post-session visual tokens, matching the polished sibling
// cards (MED-1050–1054). Structure/content is unchanged; this is styling only.
const TITLE_COLOR = '#1A1A2E';
const LABEL_COLOR = '#374151';
const HELPER_COLOR = '#9CA3AF';
const FIELD_BORDER = '#E5E7EB';
const FIELD_TEXT = '#374151';

// Full-bleed section divider (header / footer), matching the coach feedback
// card. The card pads with `padding="lg"`, so the rule is pulled back out to
// the card edges.
const BLEED_DIVIDER_STYLE = {
  borderTop: `1px solid ${FIELD_BORDER}`,
  marginInline: 'calc(-1 * var(--mantine-spacing-lg))',
} as const;

interface Props {
  sessionId: string;
  initial: ParticipantFeedbackPayload;
  /**
   * MED-1063 (AC3) — false when the participant was marked `absent`. The form
   * is suppressed (an absent notice shows instead) and writes are also blocked
   * server-side. Defaults to true so non-absent participants are unaffected.
   * Already-submitted feedback (AC5) still renders read-only regardless.
   */
  available?: boolean;
  /** Closed/open state for the accordion-style card (AC2, AC6). */
  expanded: boolean;
  onToggleExpand: () => void;
  /** Called after a successful submit (parent collapses step + bumps stepper). */
  onSubmitted: (payload: ParticipantFeedbackPayload) => void;
  /** Bubbles the latest draft payload so the sidebar status updates live. */
  onChange: (payload: ParticipantFeedbackPayload) => void;
}

// ---------------------------------------------------------------------------
// Question configuration (MED-989 / E3.4a — Brook's Paperform, 2026-06-11)
// ---------------------------------------------------------------------------

/** The five 1–5 agreement statements (AC1), all required. */
type AgreementKey =
  | 'present_intentional'
  | 'group_understood'
  | 'felt_open'
  | 'thoughts_expanded'
  | 'action_step';

/** Every required 1–5 scale field: the five agreements + satisfaction. */
type ScaleKey = AgreementKey | 'satisfaction_rating';

/** Optional open-text fields (AC2 + the preserved what_for_next, AC4). */
type TextKey =
  | 'what_enjoyed'
  | 'what_improvements'
  | 'anything_else'
  | 'what_for_next';

/** Agreement anchors — all five displayed (AC1). Higher = stronger agreement. */
const AGREEMENT_OPTIONS: ScaleOption[] = [
  { value: 1, label: 'Strongly disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly agree' },
];

/** Satisfaction anchors — all five displayed (AC1). Higher = more satisfied. */
const SATISFACTION_OPTIONS: ScaleOption[] = [
  { value: 1, label: 'Very dissatisfied' },
  { value: 2, label: 'Dissatisfied' },
  { value: 3, label: 'Neither satisfied nor dissatisfied' },
  { value: 4, label: 'Satisfied' },
  { value: 5, label: 'Very satisfied' },
];

const AGREEMENT_QUESTIONS: {
  key: AgreementKey;
  label: string;
  testid: string;
}[] = [
  {
    key: 'present_intentional',
    label: 'I was present and intentional in this session.',
    testid: 'feedback-present-intentional',
  },
  {
    key: 'group_understood',
    label: 'The group understood my challenges.',
    testid: 'feedback-group-understood',
  },
  {
    key: 'felt_open',
    label: 'I felt that I could be open in the group.',
    testid: 'feedback-felt-open',
  },
  {
    key: 'thoughts_expanded',
    label:
      'My thoughts and views have been expanded as a result of the discussion.',
    testid: 'feedback-thoughts-expanded',
  },
  {
    key: 'action_step',
    label:
      'I came away from the group with an action step to help me move forward on something I care about.',
    testid: 'feedback-action-step',
  },
];

/** Open-text prompts, in form order. what_for_next (AC4) is leader-visible. */
const OPEN_TEXT_QUESTIONS: {
  key: TextKey;
  label: string;
  description?: string;
  testid: string;
}[] = [
  {
    key: 'what_enjoyed',
    label: 'What did you enjoy about the session?',
    testid: 'feedback-what-enjoyed',
  },
  {
    key: 'what_improvements',
    label: 'What improvements can we make for future sessions?',
    testid: 'feedback-what-improvements',
  },
  {
    key: 'anything_else',
    label: 'Anything else you’d like to share?',
    testid: 'feedback-anything-else',
  },
  {
    key: 'what_for_next',
    label: 'What do you want from the next session?',
    description:
      'A question, a topic, a feeling you want to bring. Your session leader sees this to prepare.',
    testid: 'feedback-what-for-next',
  },
];

/**
 * Step 1 — Leave Feedback card (MED-806 AC3-AC6; content updated by MED-989).
 *
 * Renders Medley's real feedback questions (Brook's Paperform): five 1–5
 * agreement scales + a satisfaction scale (all required, AC1), three optional
 * open-text prompts (AC2), and the preserved forward-looking "next session"
 * field (AC4). Autosave / Save Draft / Submit and the lock-after-submit
 * behavior are unchanged from E3.4 (AC5).
 *
 * After Submit (`status === 'submitted'`), the entire card becomes read-only.
 */
export function LeaveFeedbackCard({
  sessionId,
  initial,
  available = true,
  expanded,
  onToggleExpand,
  onSubmitted,
  onChange,
}: Props) {
  const [draft, setDraft] = useState<ParticipantFeedbackPayload>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSubmitted = draft.status === 'submitted';

  // MED-1063 (AC3): an absent participant no longer gets the editable form.
  // Retained submitted feedback (AC5) still renders read-only, so only the
  // not-yet-submitted case shows the absent notice in place of the form.
  const showAbsentNotice = !available && !isSubmitted;

  const {
    state: autosaveState,
    scheduleSave,
    flush,
  } = useFeedbackAutosave({
    sessionId,
    disabled: isSubmitted,
    onSaved: payload => {
      setDraft(payload);
      onChange(payload);
    },
  });

  const updateField = useCallback(
    <K extends keyof ParticipantFeedbackPayload>(
      key: K,
      value: ParticipantFeedbackPayload[K]
    ) => {
      setDraft(prev => {
        const next = { ...prev, [key]: value };
        onChange(next);
        return next;
      });
    },
    [onChange]
  );

  const onPickScale = (key: ScaleKey, value: number) => {
    if (isSubmitted) return;
    updateField(key, value);
    scheduleSave({ [key]: value } as UpsertFeedbackDraftRequest);
  };

  const onTextChange =
    (key: TextKey) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isSubmitted) return;
      const value = e.currentTarget.value;
      updateField(key, value);
      scheduleSave({ [key]: value } as UpsertFeedbackDraftRequest);
    };

  const onSaveDraftClick = async () => {
    await flush();
  };

  const onSubmitClick = async () => {
    // AC1 — all six scales (satisfaction + five agreement) are required.
    const missingScale =
      draft.satisfaction_rating == null ||
      AGREEMENT_QUESTIONS.some(q => draft[q.key] == null);
    if (missingScale) {
      setSubmitError('Please answer all rating questions before submitting.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      // Force any in-flight autosave to land first so the server holds the
      // latest text values before submission.
      await flush();
      const result = await participantPostSessionApi.submitFeedback(sessionId, {
        satisfaction_rating: draft.satisfaction_rating as number,
        present_intentional: draft.present_intentional as number,
        group_understood: draft.group_understood as number,
        felt_open: draft.felt_open as number,
        thoughts_expanded: draft.thoughts_expanded as number,
        action_step: draft.action_step as number,
        what_enjoyed: draft.what_enjoyed,
        what_improvements: draft.what_improvements,
        anything_else: draft.anything_else,
        what_for_next: draft.what_for_next,
      });
      setDraft(result);
      onSubmitted(result);
    } catch (err) {
      const e = err as { message?: string };
      setSubmitError(
        e?.message ?? 'Failed to submit feedback. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const autosaveLabel =
    autosaveState === 'saving'
      ? 'Saving…'
      : autosaveState === 'saved'
        ? 'Auto-saved'
        : autosaveState === 'error'
          ? 'Save failed — try again'
          : autosaveState === 'locked'
            ? 'Locked'
            : 'Auto-saved as you type';

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      data-testid="card-leave-feedback"
      data-expanded={expanded ? 'true' : 'false'}
      header={
        <UnstyledButton
          onClick={onToggleExpand}
          style={{ width: '100%' }}
          aria-expanded={expanded}
          data-testid="card-leave-feedback-header"
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap={8} align="center" wrap="nowrap">
              <FileText
                size={18}
                color={TITLE_COLOR}
                aria-hidden="true"
                style={{ marginTop: -4 }}
              />
              <Text fz={15} fw={700} c={TITLE_COLOR}>
                Leave Feedback
              </Text>
              {isSubmitted && (
                <Group gap={4} c="medleyGreen">
                  <CheckCircle size={14} />
                  <Text size="xs" fw={600} tt="uppercase">
                    Submitted
                  </Text>
                </Group>
              )}
            </Group>
            <Group gap={4} c={HELPER_COLOR} wrap="nowrap">
              <Eye size={14} />
              <Text fz={12}>
                {/* AC7 — these responses are Medley measurement data, not shared
                  directly with the leader. */}
                Shared with Medley
              </Text>
            </Group>
          </Group>
        </UnstyledButton>
      }
    >
      {expanded && showAbsentNotice && (
        <Stack gap="lg">
          <Box style={BLEED_DIVIDER_STYLE} />
          {/* MED-1063 (AC3): feedback is not offered to an absent participant. */}
          <Alert
            icon={<AlertTriangle size={16} />}
            color="gray"
            variant="light"
            radius="md"
            data-testid="feedback-absent-notice"
          >
            You were marked absent from this session, so post-session feedback
            isn’t available. Feedback is only collected from participants who
            attended.
          </Alert>
        </Stack>
      )}

      {expanded && !showAbsentNotice && (
        <Stack gap="lg">
          <Box style={BLEED_DIVIDER_STYLE} />
          {/* AC1 — five agreement scales, all required */}
          {AGREEMENT_QUESTIONS.map(q => (
            <ScaleQuestion
              key={q.key}
              label={q.label}
              value={draft[q.key]}
              options={AGREEMENT_OPTIONS}
              disabled={isSubmitted}
              onPick={value => onPickScale(q.key, value)}
              testid={q.testid}
            />
          ))}

          {/* AC1 — satisfaction scale, required */}
          <ScaleQuestion
            label="How satisfied are you with your overall Medley experience?"
            value={draft.satisfaction_rating}
            options={SATISFACTION_OPTIONS}
            disabled={isSubmitted}
            onPick={value => onPickScale('satisfaction_rating', value)}
            testid="feedback-rating"
          />

          {/* AC2 + AC4 — open-text prompts (all optional) */}
          {OPEN_TEXT_QUESTIONS.map(q => (
            <Stack key={q.key} gap={6}>
              <Group gap={8} align="baseline" wrap="wrap">
                <Text fz={13} fw={600} c={LABEL_COLOR}>
                  {q.label}
                </Text>
                {q.description && (
                  <Text fz={12} c={HELPER_COLOR}>
                    {q.description}
                  </Text>
                )}
              </Group>
              <Textarea
                value={draft[q.key]}
                disabled={isSubmitted}
                onChange={onTextChange(q.key)}
                autosize
                minRows={3}
                data-testid={q.testid}
                styles={{
                  input: {
                    borderColor: FIELD_BORDER,
                    borderRadius: 8,
                    color: FIELD_TEXT,
                    fontSize: 13,
                    lineHeight: 1.55,
                  },
                }}
              />
            </Stack>
          ))}

          {/* AC7 — leader visibility: feedback goes to Medley; the only field
              the session leader sees directly is the next-session intention. */}
          <Alert
            icon={<Info size={16} />}
            variant="light"
            radius="md"
            data-testid="feedback-visibility-banner"
            styles={{
              root: {
                backgroundColor: '#DBEAFE',
                border: '1px solid #3B82B0',
              },
              icon: { color: '#203358' },
              message: { color: '#203358', fontSize: 13, lineHeight: 1.5 },
            }}
          >
            Your ratings and written answers go to Medley to help us measure and
            improve the experience — they aren’t shared directly with your
            session leader. The exception is “What do you want from the next
            session?”, which your session leader sees to help prepare.
          </Alert>

          {submitError && (
            <Alert
              color="red"
              variant="light"
              radius="md"
              data-testid="feedback-submit-error"
            >
              {submitError}
            </Alert>
          )}

          {!isSubmitted && (
            <>
              <Box style={BLEED_DIVIDER_STYLE} />
              <Group justify="space-between" align="center" wrap="wrap">
                <Text
                  fz={12}
                  c={HELPER_COLOR}
                  data-testid="feedback-autosave-status"
                >
                  {autosaveLabel}
                </Text>
                <Group gap="sm">
                  <Button
                    variant="default"
                    onClick={onSaveDraftClick}
                    data-testid="feedback-save-draft"
                  >
                    Save Draft
                  </Button>
                  <Button
                    onClick={onSubmitClick}
                    loading={submitting}
                    data-testid="feedback-submit"
                    styles={{
                      root: {
                        backgroundColor: POST_SESSION_PRIMARY_BG,
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Submit Feedback
                  </Button>
                </Group>
              </Group>
            </>
          )}
        </Stack>
      )}
    </ContentCard>
  );
}
